<template>
  <div class="grey-lighten-1 py-4 px-2 rounded ma-0 mb-6">
    <component
      :is="componentMap[item]"
      v-for="item in items"
      :key="item"
      v-bind="componentProps[item] ?? {}"
    />
    <template v-if="!buttonMode">
      <v-row align="center" class="mt-3">
        <v-col cols="auto" class="d-flex flex-column ml-4 mb-1">
          <v-checkbox v-model="checkboxState" :error="touched && !checkboxState" hide-details />
        </v-col>
        <v-col class="ml-1">
          <span>{{ $t('alertInfo.iAgree') }}</span>
        </v-col>
      </v-row>
      <div v-if="touched && !checkboxState" class="error--text ml-4">
        <span>{{ $t('alertInfo.errorText') }}</span>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onBeforeUnmount, getCurrentInstance, type Component } from 'vue';
import EmergencyInfo from '@/components/Atoms/EmergencyInfo.vue';
import EPrescriptionInfo from '@/feature/e-prescription/components/EPrescriptionInfo.vue';
import QuestionPriceInfo from '@/feature/questions/components/QuestionPriceInfo.vue';

interface DisclaimerInputData {
  id?: string;
  props?: {
    items?: string[];
    buttonMode?: boolean;
  };
  [key: string]: unknown;
}

const props = defineProps<{
  inputData?: DisclaimerInputData;
  formName?: string | null;
  stepId?: number | string | null;
  value?: boolean | string | number | null;
}>();

const emit = defineEmits<{
  'update:value': [val: boolean];
  'valid-change': [val: boolean];
}>();

const instance = getCurrentInstance()!.proxy;
const store = instance.$store;
const eventHub = instance.$eventHub;

const touched = ref(false);

const componentMap: Record<string, Component> = {
  emergency: EmergencyInfo,
  ePrescription: EPrescriptionInfo,
  questionPrice: QuestionPriceInfo,
  ePrescriptionOrder: EPrescriptionInfo
};

const componentProps: Record<string, Record<string, unknown>> = {
  ePrescriptionOrder: { context: 'order' }
};

const items = computed(() => props.inputData?.props?.items ?? ['emergency']);
const buttonMode = computed(() => !!props.inputData?.props?.buttonMode);

const checkboxState = computed({
  get() {
    return store.state.forms.emergencyDisclaimer.isEmergencyChecked;
  },
  set(value: boolean) {
    touched.value = true;
    store.commit('forms/emergencyDisclaimer/setIsEmergencyChecked', value);
    syncUp(value);
  }
});

function syncUp(value: boolean | string | number | null | undefined) {
  const ok = !!value;
  emit('update:value', ok);
  emit('valid-change', ok);
}

function markAsTouched() {
  touched.value = true;
}

// Watch value prop
const unwatch = instance.$watch(
  () => props.value,
  (value: boolean | string | number | null | undefined) => {
    if (value === undefined || value === null) {
      return;
    }
    const bool = !!value;
    if (store.state.forms.emergencyDisclaimer.isEmergencyChecked !== bool) {
      store.commit('forms/emergencyDisclaimer/setIsEmergencyChecked', bool);
    }
    syncUp(bool);
  },
  { immediate: true }
);

onMounted(() => {
  syncUp(store.state.forms.emergencyDisclaimer.isEmergencyChecked);

  const id = props.inputData?.id;
  if (id) {
    eventHub.$on(`validateInput_${id}_${props.stepId}`, markAsTouched);
  }
});

onBeforeUnmount(() => {
  unwatch();
  const id = props.inputData?.id;
  if (id) {
    eventHub.$off(`validateInput_${id}_${props.stepId}`, markAsTouched);
  }
});
</script>
